import { useFormik } from "formik";
import { useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { bulkUploadCertificates } from "../../api";

const useBulkUpload = ({ refetch, handleClose }) => {
  const [fileError, setFileError] = useState("");
  const branchId = useSelector(
    (state) => state.global?.currentBranch?.[0]?._id,
  );

  const formik = useFormik({
    initialValues: {
      assetsImages: [],
      remark: "",
    },
    validationSchema: Yup.object({
      assetsImages: Yup.array().min(1, "Select at least one image"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      toast.warning("Upload is in progress...");
      const formData = new FormData();
      values.assetsImages.forEach((img, i) => {
        formData.append(`files[${i}]`, img.file);
      });
      formData.append("remarks", values.remark || "");
      formData.append("branch_id", branchId);

      const response = await bulkUploadCertificates(formData);
      const payload = response?.data ?? response;
      if (payload?.status === true) {
        toast.success(payload?.msg || "Bulk upload completed");
        handleClose();
        refetch();
      } else {
        toast.error(payload?.msg || "Something went wrong");
      }
      setSubmitting(false);
    },
  });

  const getFieldError = (fieldName) => {
    if (fieldName === "assetsImages" && fileError) return fileError;
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  const handleAssetsImagesChange = (e) => {
    const files = e?.target?.files;
    if (!files?.length) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 5 * 1024 * 1024;
    const errors = [];
    const validFiles = [];

    Array.from(files).forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`File ${file.name} is not a valid image type`);
      } else if (file.size > maxSize) {
        errors.push(`File ${file.name} exceeds the maximum size of 5MB`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setFileError(errors[0]);
      return;
    }

    const readFiles = (filesToRead) =>
      Promise.all(
        filesToRead.map(
          (file) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve({ file, thumb: reader.result });
              };
              reader.readAsDataURL(file);
            }),
        ),
      );

    readFiles(validFiles).then((validFilesWithThumbs) => {
      const existing = formik.values.assetsImages || [];
      const combined = [...existing, ...validFilesWithThumbs];
      formik.setFieldValue("assetsImages", combined);
      setFileError("");
    });
  };

  const handleAssetsImagesDelete = (data) => {
    const remaining = (formik.values.assetsImages || []).filter(
      (item) => item?.thumb !== data?.thumb,
    );
    formik.setFieldValue("assetsImages", remaining);
  };

  return {
    formik,
    getFieldError,
    handleAssetsImagesChange,
    handleAssetsImagesDelete,
  };
};

export default useBulkUpload;
